import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import type { Message, UseFormTrigger } from 'react-hook-form';
import { currencyMapper, storeKeyMapper } from '@shared/utils/src/ct/index';
import type { Cart, CartError, LineItem } from '@storefront/lib-global/codegen/types';
import { useCartContext } from '@storefront/lib-global/context/cartContext';
import { useLocaleContext } from '@storefront/lib-global/context/localeContext';
import { useOverlayContext } from '@storefront/lib-global/context/overlayContext';
import { useStaticContext } from '@storefront/lib-global/context/staticContext';
import { CartLoader } from '@storefront/lib-global/globalComponents/cart/cartLoader';
import { useAddToBag } from '@storefront/lib-global/hooks/useAddToBag';
import { useLoadingCursor } from '@storefront/lib-global/hooks/useLoadingCursor';
import { useScreenChanges } from '@storefront/lib-global/hooks/useScreenChanges';
import { CheckIcon, CloseSmall } from '@storefront/lib-global/icons';
import { sendAlgoliaInsightsAddToCartEvent } from '@storefront/lib-global/services/algolia/insights/integrations';
import { sendAddToCartFromGiftCardEvent } from '@storefront/lib-global/utils/gtm/events/add_to_cart/sendAddToCartFromGiftCardEvent';
import { sendAddToCartFromPDPEvent } from '@storefront/lib-global/utils/gtm/events/add_to_cart/sendAddToCartFromPDPEvent';
import { getCtLocale } from '@storefront/lib-global/utils/internationalization';
import { useOccasionAndDenominationContext, useSizeAndWidthContext } from '../../../context';
import { useCreateCart } from '../../../hooks/useCreateCart';
import { hasBackInStockFlag, isOutOfStock } from '../../../utils';
import type { GiftCardFormValues } from '../giftCardInfo/GiftCardInfo';
import {
  AddToBagWrapper,
  BellIcon,
  ErrorMessage,
  IconContainer,
  LoaderAnimation,
  StyledAddToBag,
} from './AddToBag.styles';

type GiftCardATBProps = {
  isGiftCard?: boolean;
  to?: string;
  from?: string;
  email?: string;
  message?: Message;
  trigger?: UseFormTrigger<GiftCardFormValues>;
  isValid?: boolean;
};

export function AddToBag({ isGiftCard, to, from, email, message, trigger, isValid }: GiftCardATBProps) {
  const { t } = useTranslation(['lib-products-common']);
  const { locale } = useLocaleContext();
  const { miniCart, anonymousId, cartId, isCartLoading } = useCartContext();
  const { previousURL, baseUrl, storeData } = useStaticContext();

  const { skuCap } = { ...storeData };
  const { selectedSku, setError, selectedSize, selectedWidth, variants } = useSizeAndWidthContext();
  const { selectedGiftCardSku, selectedPriceDenomination, setPriceDenominationError } =
    useOccasionAndDenominationContext();
  const { setIsMiniCartOpen } = useOverlayContext();

  const [isPdpSkuCap, setIsPdpSkuCap] = useState(false);
  const [addToBagError, setAddToBagError] = useState(false);
  const [animationAddToBagFinished, setAnimationAddToBagFinished] = useState(false);
  const [lineItemError, setLineItemError] = useState(false);

  const { addToBag, addToBagLoading, addToBagData } = useAddToBag();
  const { createCart } = useCreateCart();

  const { elementRef, isRatioExceeded } = useScreenChanges('height', 0.3);

  const isCurrentlyUnavailable = isOutOfStock(selectedSize, selectedWidth, variants);
  const backInStockNotification =
    isCurrentlyUnavailable && variants.length === 1 ? hasBackInStockFlag(selectedSize, selectedWidth, variants) : false;

  const checkForLineItemError = (errors: CartError[]) => errors?.some((err) => err?.__typename === 'LineItemError');

  const handleAddToBag = async () => {
    if (!selectedSku) {
      setError([...(!selectedSize ? ['size'] : []), ...(!selectedWidth ? ['width'] : [])]);
    } else {
      setAddToBagError(false);

      const input = {
        currency: currencyMapper[locale],
        storeKey: storeKeyMapper(locale),
        locale: getCtLocale(locale),
        sku: selectedSku,
        quantity: 1,
        cartId,
        ...(anonymousId && { anonymousId }),
      };

      try {
        if (cartId) {
          await addToBag({
            variables: { input },
            onCompleted: ({ addToCart }) => {
              const { errors } = addToCart;
              const hasLineItemError = !!errors?.length && checkForLineItemError(errors);
              if (hasLineItemError) {
                setLineItemError(true);
              } else {
                const product = addToCart?.lineItems?.find((lineItem) => lineItem.variant.sku === input.sku) || '';
                sendAlgoliaInsightsAddToCartEvent(product as LineItem, locale, previousURL as string);
                sendAddToCartFromPDPEvent(selectedSku, addToCart as Cart, baseUrl);
              }
            },
            onError: () => setAddToBagError(true),
          });
          return;
        }

        await createCart({
          onCompleted: async ({ createCart: createCartData }) => {
            const { id: createCartId, anonymousId: createCartAnonId } = { ...createCartData };

            await addToBag({
              variables: {
                input: {
                  ...input,
                  cartId: createCartId,
                  anonymousId: createCartAnonId,
                },
              },
              onCompleted: ({ addToCart }) => {
                const { errors } = addToCart;
                const hasLineItemError = !!errors?.length && checkForLineItemError(errors);
                if (hasLineItemError) {
                  setLineItemError(true);
                } else {
                  const product = addToCart?.lineItems?.find((lineItem) => lineItem.variant.sku === input.sku) || '';
                  sendAlgoliaInsightsAddToCartEvent(product as LineItem, locale, previousURL as string);
                  sendAddToCartFromPDPEvent(selectedSku, addToCart as Cart, baseUrl);
                }
              },
              onError: () => setAddToBagError(true),
            });
          },
          onError: () => setAddToBagError(true),
        });
      } catch (e) {
        setAddToBagError(true);
      }
    }
  };

  useEffect(() => {
    const sameItemSku = miniCart?.lineItems?.find(
      (lineItem) => lineItem.variant.sku === selectedSku || lineItem.variant.sku === selectedGiftCardSku,
    );

    if (sameItemSku?.quantity === skuCap) {
      setIsPdpSkuCap(true);
    } else {
      setIsPdpSkuCap(false);
    }
  }, [miniCart?.lineItems, selectedGiftCardSku, selectedSku, skuCap]);

  const areFieldsValid = () => {
    if (trigger) trigger('email', { shouldFocus: !!selectedPriceDenomination });

    if (!selectedPriceDenomination) {
      setPriceDenominationError(true);
      const priceElement = document.getElementById('price-denominations');
      priceElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });

      return false;
    }

    return true;
  };

  const handleGiftCardATB = async () => {
    if (!areFieldsValid() || !isValid) return;
    setAddToBagError(false);

    const input = {
      currency: currencyMapper[locale],
      storeKey: storeKeyMapper(locale),
      locale: getCtLocale(locale),
      cartId,
      quantity: 1,
      sku: selectedGiftCardSku as string,
      giftCardSender: from,
      giftCardRecipient: to,
      giftCardRecipientEmail: email,
      giftCardMessage: message,
      ...(anonymousId && { anonymousId }),
    };

    try {
      if (cartId) {
        await addToBag({
          variables: { input },
          onCompleted: ({ addToCart }) => {
            const { errors } = addToCart;
            const hasLineItemError = !!errors?.length && checkForLineItemError(errors);
            if (hasLineItemError) {
              setLineItemError(true);
            } else {
              sendAddToCartFromGiftCardEvent(baseUrl, selectedGiftCardSku, addToCart as Cart);
              setPriceDenominationError(false);
            }
          },
          onError: () => setAddToBagError(true),
        });
        return;
      }

      await createCart({
        onCompleted: async ({ createCart: createCartData }) => {
          const { id: createCartId, anonymousId: createCartAnonId } = { ...createCartData };
          if (selectedPriceDenomination && isValid) {
            await addToBag({
              variables: {
                input: {
                  ...input,
                  cartId: createCartId,
                  anonymousId: createCartAnonId,
                  quantity: 1,
                  sku: selectedGiftCardSku as string,
                },
              },
              onCompleted: ({ addToCart }) => {
                const { errors } = addToCart;
                const hasLineItemError = !!errors?.length && checkForLineItemError(errors);
                if (hasLineItemError) {
                  setLineItemError(true);
                } else {
                  sendAddToCartFromGiftCardEvent(baseUrl, selectedGiftCardSku, addToCart as Cart);
                }
              },
              onError: () => setAddToBagError(true),
            });
          }
        },
        onError: () => setAddToBagError(true),
      });
    } catch (e) {
      setAddToBagError(true);
    }
  };

  useEffect(() => {
    const slidingBackground = document.querySelector('.sliding-background');
    const hasReduceMotion = window?.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const handleAnimationEnd = () => {
      setAnimationAddToBagFinished(true);
    };

    if (addToBagLoading) {
      slidingBackground?.addEventListener('animationend', handleAnimationEnd);

      if (hasReduceMotion) {
        handleAnimationEnd();
      }
    }
    return () => {
      slidingBackground?.removeEventListener('animationend', handleAnimationEnd);
    };
  }, [addToBagLoading]);

  useLoadingCursor(addToBagLoading);

  useEffect(() => {
    if (animationAddToBagFinished && !addToBagLoading) {
      const timer = setTimeout(() => {
        setAnimationAddToBagFinished(false);
      }, 500);

      return () => {
        clearTimeout(timer);
      };
    }
    return () => {};
  }, [addToBagData?.addToCart, addToBagError, addToBagLoading, animationAddToBagFinished, setIsMiniCartOpen]);

  return (
    <AddToBagWrapper ref={elementRef} $isZoomed={isRatioExceeded}>
      <StyledAddToBag
        disabled={
          (isCurrentlyUnavailable && !backInStockNotification && !isGiftCard) ||
          isPdpSkuCap ||
          addToBagLoading ||
          isCartLoading ||
          lineItemError
        }
        type="button"
        onClick={!isGiftCard ? handleAddToBag : handleGiftCardATB}
        data-testid="addToBagButton"
        $addToBagLoading={addToBagLoading}
      >
        {backInStockNotification && <BellIcon />}
        {isCurrentlyUnavailable &&
          !isGiftCard &&
          (backInStockNotification ? t('pdp.notify.me') : t('pdp.currently.unavailable'))}
        {addToBagLoading && <LoaderAnimation className="sliding-background" />}
        {animationAddToBagFinished && !addToBagLoading ? (
          <IconContainer $isPdpSkuCap={isPdpSkuCap}>
            {!addToBagLoading && (addToBagError || lineItemError) && <CloseSmall />}{' '}
            {!addToBagLoading && !addToBagError && !lineItemError && <CheckIcon />}{' '}
          </IconContainer>
        ) : (
          (!isCurrentlyUnavailable || isGiftCard) && <div>{isCartLoading ? <CartLoader /> : t('pdp.add.to.bag')}</div>
        )}
      </StyledAddToBag>
      {isPdpSkuCap && (
        <ErrorMessage role="alert" data-testid="pdpSkuCapError">
          {t('pdp.add.to.bag.cap.limit', { skuCap })}
        </ErrorMessage>
      )}
      {addToBagError && <ErrorMessage role="alert">{t('pdp.add.to.bag.error')}</ErrorMessage>}
      {lineItemError && <ErrorMessage role="alert">{t('pdp.add.to.bag.lineItemError')}</ErrorMessage>}
    </AddToBagWrapper>
  );
}

export default AddToBag;
